import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import { ContentError } from '@/components/ContentError'
import { Icon } from '@/components/Icon'
import { MinidownContent } from '@/components/MinidownContent'
import { Tooltip } from '@/components/Tooltip'
import { links } from '@/utils/link'
import { match } from '@/utils/pattern-match-util'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { consultationSteps } from '@/features/jobs/details/consultation-state'
import {
  NextStepCard,
  NextStepCardActions,
  NextStepCardBody,
  NextStepCardCompleted,
  NextStepCardDescription,
  NextStepCardEyebrow,
  NextStepCardFooter,
  NextStepCardInfo,
  NextStepCardProgress,
  NextStepCardSkeleton,
  NextStepCardSteps,
  NextStepCardTitle,
  NextStepCardWaitingOn,
} from '@/features/jobs/details/components/NextStepCard'
import {
  AdminProgressionActionsContainer,
  ClientProgressionActionsContainer,
  ProviderProgressionActionsContainer,
  SupportingClientProgressionActionsContainer,
} from '@/features/jobs/details/components/JobProgressionCard/ProgressionActionsContainers'
import { CollapseSecondaryActionsContext } from '@/features/jobs/details/components/JobProgressionCard/collapse-secondary-actions-context'
import { ConsultationActionsContainer } from '@/features/jobs/details/components/ConsultationActions/ConsultationActionsContainer'
import {
  getPrimaryActionTestId,
  getSecondaryActionTestId,
} from '@/features/jobs/details/components/BookkeepingProgressionCard/bookkeeping-step-test-ids'
import { BookkeepingTrackerCardContainer } from '@/features/jobs/details/components/BookkeepingTrackerCard/BookkeepingTrackerCardContainer'
import { CardBand } from '@/features/jobs/details/components/CardBand'

interface NextStepContainerProps {
  vm: JobDetailsViewModel
}

/**
 * The job's next step under the header: one card for whichever stage the job
 * is in, with a call or a pending scope change attached underneath.
 */
export const NextStepContainer = observer(function NextStepContainer({
  vm,
}: NextStepContainerProps) {
  const footers = <ScopeChangeFooterContainer vm={vm} />

  if (!vm.hasNextStep) {
    return vm.scopeChangesVm.isBannerVisible ? (
      <NextStepCard>{footers}</NextStepCard>
    ) : null
  }

  return match(vm.visibleCard, {
    JobProgression: () => (
      <ProgressionNextStepContainer vm={vm} footers={footers} />
    ),
    JobProgressionConsultation: () => (
      <ProgressionNextStepContainer
        vm={vm}
        footers={
          <>
            <ConsultationFooterContainer vm={vm} />
            {footers}
          </>
        }
      />
    ),
    ConsultationCall: () => (
      <ConsultationNextStepContainer vm={vm} footers={footers} />
    ),
    CompleteOnboarding: () => (
      <OnboardingNextStepContainer vm={vm} footers={footers} />
    ),
    InfoGathering: () => (
      <InfoGatheringNextStepContainer vm={vm} footers={footers} />
    ),
    Task: () => <TaskNextStepContainer vm={vm} footers={footers} />,
    Bookkeeping: () => (
      <BookkeepingNextStepContainer vm={vm} footers={footers} />
    ),
  })
})

interface StepContainerProps {
  vm: JobDetailsViewModel
  footers: ReactNode
}

const ProgressionNextStepContainer = observer(
  function ProgressionNextStepContainer({ vm, footers }: StepContainerProps) {
    const progression = vm.jobProgressionVm
    const onViewSteps = progression.hasFlowSteps
      ? progression.stepsDialogVm.show
      : undefined

    const renderCard = () => (
      <NextStepCard testId="job-progression-row">
        {vm.job.completed ? (
          <NextStepCardCompleted
            title="Job completed"
            onViewSteps={onViewSteps}
            testId="job-progression-card-title"
            viewStepsTestId="job-progression-view-steps"
          />
        ) : (
          <>
            {progression.currentFlowStepIndex >= 0 && (
              <NextStepCardSteps
                current={progression.currentFlowStepIndex + 1}
                total={progression.flowSteps.length}
                tone={progression.currentStepTone}
              />
            )}
            <NextStepCardBody>
              <NextStepCardInfo>
                <NextStepCardEyebrow
                  tag={progression.isExtensionFlow ? 'Extension' : undefined}
                >
                  {progression.currentStepSummary}
                </NextStepCardEyebrow>
                <NextStepCardTitle
                  onViewSteps={onViewSteps}
                  testId="job-progression-card-title"
                  viewStepsTestId="job-progression-view-steps"
                >
                  {progression.currentStepInfo?.title}
                </NextStepCardTitle>
                <NextStepCardDescription>
                  <MinidownContent
                    value={
                      vm.seesClientProgression
                        ? progression.currentStepDescriptions.client
                        : progression.currentStepDescriptions.pro
                    }
                  />
                </NextStepCardDescription>
              </NextStepCardInfo>
              <NextStepCardActions>
                <CollapseSecondaryActionsContext.Provider value>
                  {vm.isProviderCollaborator && (
                    <ProviderProgressionActionsContainer
                      vm={progression}
                      jobVm={vm}
                      canCompleteJob={vm.canCompleteJobProgression}
                    />
                  )}
                  {vm.isChampionClientCollaborator && (
                    <ClientProgressionActionsContainer vm={vm} />
                  )}
                  {vm.canSetReadyToFile && (
                    <AdminProgressionActionsContainer vm={vm} />
                  )}
                  {vm.canApproveDraftAsSupportingClient && (
                    <SupportingClientProgressionActionsContainer vm={vm} />
                  )}
                </CollapseSecondaryActionsContext.Provider>
                {vm.progressionWaitingOn && (
                  <NextStepCardWaitingOn
                    subject={vm.progressionWaitingOn}
                    label={`Waiting on ${vm.progressionWaitingOn.displayName}`}
                    testId="next-step-waiting-on"
                  />
                )}
              </NextStepCardActions>
            </NextStepCardBody>
          </>
        )}
        {footers}
      </NextStepCard>
    )

    return progression.fetch.match({
      pending: () =>
        progression.isRevalidating ? (
          renderCard()
        ) : (
          <NextStepCardSkeleton>{footers}</NextStepCardSkeleton>
        ),
      rejected: (err) => (
        <ContentError
          size="section"
          title="An error occurred while loading the job progression."
          error={err}
        />
      ),
      resolved: renderCard,
    })
  },
)

const ConsultationNextStepContainer = observer(
  function ConsultationNextStepContainer({ vm, footers }: StepContainerProps) {
    const state = vm.consultationVm.state
    if (!state) {
      return null
    }

    return (
      <NextStepCard testId="consultation-line">
        {state.phase === 'complete' ? (
          <NextStepCardCompleted
            title={state.title}
            testId="current-task-title"
          />
        ) : (
          <>
            <NextStepCardSteps
              current={state.step + 1}
              total={consultationSteps.length}
              tone={state.phase === 'live' ? 'live' : 'default'}
            />
            <NextStepCardBody>
              <NextStepCardInfo>
                <NextStepCardEyebrow>
                  {vm.consultationVm.stepLabel}
                </NextStepCardEyebrow>
                <NextStepCardTitle testId="current-task-title">
                  {state.title}
                </NextStepCardTitle>
                <NextStepCardDescription>
                  {state.description}
                </NextStepCardDescription>
              </NextStepCardInfo>
              <NextStepCardActions>
                <ConsultationActionsContainer vm={vm} state={state} />
              </NextStepCardActions>
            </NextStepCardBody>
          </>
        )}
        {footers}
      </NextStepCard>
    )
  },
)

const OnboardingNextStepContainer = observer(
  function OnboardingNextStepContainer({ vm, footers }: StepContainerProps) {
    const questionsCompleted = vm.questionsCompleted

    return (
      <NextStepCard testId="complete-onboarding-card">
        <NextStepCardProgress done={questionsCompleted ? 50 : 0} pending={50} />
        <NextStepCardBody>
          <NextStepCardInfo>
            <NextStepCardTitle>
              {questionsCompleted ? 'Review and submit' : 'Complete onboarding'}
            </NextStepCardTitle>
            <NextStepCardDescription>
              {questionsCompleted
                ? 'Review responses and submit the job.'
                : 'Capture all outstanding responses to proceed with the job.'}
            </NextStepCardDescription>
          </NextStepCardInfo>
          <NextStepCardActions>
            {questionsCompleted ? (
              <>
                {vm.billingVm.canManageBilling && (
                  <Button label="Checkout Job" to={links.checkout(vm.job.id)} />
                )}
                <Button
                  label="Submit Job"
                  variant="primary"
                  onPress={vm.actionsVm.submitJobDialogVm.show}
                  testId="complete-onboarding-card-submit-button"
                />
              </>
            ) : (
              <Button
                label="Edit Responses"
                variant="primary"
                onPress={vm.actionsVm.editDraft}
                testId="complete-onboarding-card-edit-responses-button"
              />
            )}
          </NextStepCardActions>
        </NextStepCardBody>
        {footers}
      </NextStepCard>
    )
  },
)

const InfoGatheringNextStepContainer = observer(
  function InfoGatheringNextStepContainer({ vm, footers }: StepContainerProps) {
    const infoGathering = vm.capturedInformationVm.infoGatheringCardVm
    const client = vm.job.championClient?.member

    return (
      <NextStepCard>
        <NextStepCardBody>
          <NextStepCardInfo>
            <NextStepCardTitle>{infoGathering.title}</NextStepCardTitle>
            <NextStepCardDescription>
              {infoGathering.description}
            </NextStepCardDescription>
          </NextStepCardInfo>
          <NextStepCardActions>
            {infoGathering.canOfferCompletion ? (
              <Button
                label="Complete Info Gathering"
                variant="primary"
                testId="info-gathering-card-complete-button"
                onPress={infoGathering.completeInfoGatheringDialogVm.modal.show}
                isDisabled={!infoGathering.canCompleteInfoGathering}
              />
            ) : (
              client && (
                <NextStepCardWaitingOn
                  subject={client}
                  label={`Waiting on ${client.displayName}`}
                  testId="next-step-waiting-on"
                />
              )
            )}
          </NextStepCardActions>
        </NextStepCardBody>
        {footers}
      </NextStepCard>
    )
  },
)

const TaskNextStepContainer = observer(function TaskNextStepContainer({
  vm,
  footers,
}: StepContainerProps) {
  if (!vm.collaboratorsVm.currentUserCollaborator) {
    return null
  }

  return (
    <NextStepCard testId="current-task-new">
      <NextStepCardBody>
        <NextStepCardInfo>
          <NextStepCardTitle testId="current-task-title-new">
            Current task
          </NextStepCardTitle>
          <NextStepCardDescription>
            <span data-test="no-task-list">You have no outstanding tasks.</span>
          </NextStepCardDescription>
        </NextStepCardInfo>
        <NextStepCardActions>
          {vm.actionsVm.canCloseAsCollaborator && (
            <Button
              label="Complete Job"
              onPress={vm.requestCloseAsCollaborator}
              testId="complete-job-new"
            />
          )}
        </NextStepCardActions>
      </NextStepCardBody>
      {footers}
    </NextStepCard>
  )
})

const BookkeepingNextStepContainer = observer(
  function BookkeepingNextStepContainer({ vm, footers }: StepContainerProps) {
    const bookkeeping = vm.bookkeepingVm.bookkeepingProgressionVm

    const renderCard = () => {
      const info = bookkeeping.bookkeepingStepInfo
      if (!info) {
        return null
      }

      return (
        <NextStepCard>
          <NextStepCardProgress
            done={info.percentDone}
            pending={info.percentPending}
          />
          <NextStepCardBody>
            <NextStepCardInfo>
              <NextStepCardEyebrow tag="Bookkeeping" />
              <NextStepCardTitle>
                {info.title}
                {info.tooltip && (
                  <Tooltip content={info.tooltip}>
                    <Icon size={14} name="info" aria-hidden="true" />
                  </Tooltip>
                )}
              </NextStepCardTitle>
              <NextStepCardDescription>
                {info.description}
              </NextStepCardDescription>
            </NextStepCardInfo>
            <NextStepCardActions>
              {info.secondaryAction && (
                <Button
                  label={info.secondaryActionText ?? ''}
                  onPress={info.secondaryAction}
                  testId={getSecondaryActionTestId(bookkeeping.bookkeepingStep)}
                />
              )}
              {info.primaryAction && (
                <Button
                  label={info.primaryActionText ?? ''}
                  variant="primary"
                  onPress={info.primaryAction}
                  testId={getPrimaryActionTestId(bookkeeping.bookkeepingStep)}
                />
              )}
            </NextStepCardActions>
          </NextStepCardBody>
          {footers}
        </NextStepCard>
      )
    }

    return (
      <>
        {bookkeeping.fetch.match({
          pending: renderCard,
          rejected: (err) => (
            <ContentError
              size="section"
              title="An error occurred while loading the bookkeeping progression."
              error={err}
            />
          ),
          resolved: renderCard,
        })}
        <CardBand>
          <BookkeepingTrackerCardContainer vm={bookkeeping} />
        </CardBand>
      </>
    )
  },
)

interface ScopeChangeFooterContainerProps {
  vm: JobDetailsViewModel
}

const ScopeChangeFooterContainer = observer(
  function ScopeChangeFooterContainer({ vm }: ScopeChangeFooterContainerProps) {
    const scopeChanges = vm.scopeChangesVm
    if (!scopeChanges.isBannerVisible) {
      return null
    }

    const banner = scopeChanges.scopeChangeBannerVm
    return (
      <NextStepCardFooter
        tone="warning"
        testId="scope-change-banner"
        actions={
          <Button
            label="View request"
            size="small"
            onPress={banner.viewRequest}
          />
        }
      >
        <b>{banner.title}.</b> {banner.summary}
        {vm.isProviderCollaborator &&
          " The job can't be completed until it's approved or declined."}
      </NextStepCardFooter>
    )
  },
)

interface ConsultationFooterContainerProps {
  vm: JobDetailsViewModel
}

const ConsultationFooterContainer = observer(
  function ConsultationFooterContainer({
    vm,
  }: ConsultationFooterContainerProps) {
    const state = vm.consultationVm.state
    if (!state || state.phase === 'complete') {
      return null
    }

    return (
      <NextStepCardFooter
        tone={state.phase === 'live' ? 'live' : 'neutral'}
        testId="consultation-line"
        actions={<ConsultationActionsContainer vm={vm} state={state} />}
      >
        <b data-test="current-task-title">{state.title}.</b> {state.description}
      </NextStepCardFooter>
    )
  },
)
